/**
 * Lender Selection Section
 * Toggle lenders for application submission
 */

import * as React from "react";
import { DomainLender } from "@porchpass/api-client";
import { ApplicationLendersSection } from "@porchpass/ui";
import { useApplicationFormStore } from "../hooks/useApplicationFormStore";
import {
  formatPorchpassBlockNotice,
  getPorchpassLenderSelectionState,
  isPorchpassBlocked,
  resolvePorchpassBlockReasons,
} from "src/utils/porchpassBlockReasons";

export const LenderSelectionSection: React.FC = () => {
  const store = useApplicationFormStore();
  const lenderError = store.fieldErrors["lenders.enabled"];
  const land = store.data.land;
  const porchpassInput = {
    ...land,
    homeState: land.propertyAddress?.state,
    borrowerCitizenships: [
      store.data.primaryBorrower.citizenship,
      ...(store.data.hasCoBorrower ? [store.data.coBorrower?.citizenship] : []),
    ],
  };
  const porchpassBlockReasons = resolvePorchpassBlockReasons(porchpassInput);
  const porchpassBlocked = isPorchpassBlocked(porchpassInput);

  const lenders = store.data.lenders.filter((l) => l.dealerEnabled);

  // Auto-deselect PorchPass when any block reason applies (non-primary only).
  React.useEffect(() => {
    if (!porchpassBlocked) return;

    const porchpass = store.data.lenders.find(
      (l) => l.lenderId === DomainLender.PorchPass,
    );
    if (!porchpass?.enabled || porchpass.isPrimary) return;

    store.toggleLender(DomainLender.PorchPass, false);
    if (porchpass.autoSubmit) {
      store.toggleLenderAutoSubmit(DomainLender.PorchPass, false);
    }
  }, [
    porchpassBlocked,
    store.data.lenders,
    store.toggleLender,
    store.toggleLenderAutoSubmit,
  ]);

  const handleToggle = (lenderId: string, checked: boolean) => {
    if (checked && lenderId === DomainLender.PorchPass && porchpassBlocked) {
      return;
    }
    if (checked && store.fieldErrors["lenders.enabled"]) {
      store.clearFieldError("lenders.enabled");
    }
    store.toggleLender(lenderId, checked);
  };

  const handleToggleAutoSubmit = (lenderId: string, autoSubmit: boolean) => {
    store.toggleLenderAutoSubmit(lenderId, autoSubmit);
  };

  return (
    <ApplicationLendersSection
      notice={
        porchpassBlocked
          ? formatPorchpassBlockNotice(porchpassBlockReasons)
          : undefined
      }
      lenders={lenders.map((l) => {
        const porchpassState =
          l.lenderId === DomainLender.PorchPass && porchpassBlocked
            ? getPorchpassLenderSelectionState(porchpassInput)
            : {
                selectionDisabled: false,
                selectionDisabledTooltip: undefined,
              };

        return {
          lenderId: l.lenderId,
          lenderName: l.lenderName,
          logoUrl: l.logoUrl,
          enabled: l.enabled,
          autoSubmit: l.autoSubmit,
          isPrimary: l.isPrimary,
          selectionDisabled: porchpassState.selectionDisabled,
          selectionDisabledTooltip: porchpassState.selectionDisabledTooltip,
        };
      })}
      onToggleEnabled={handleToggle}
      onToggleAutoSubmit={handleToggleAutoSubmit}
      error={lenderError}
    />
  );
};

export default LenderSelectionSection;
